import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth, authHeaders } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { User, Bell, Shield, Palette, Monitor, Save, Calendar, Loader2, Check } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

const AVAILABILITY_OPTIONS = [
  { value: 'available', label: 'Available', desc: 'Open to new projects and collaborations', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' },
  { value: 'manageable', label: 'Manageable', desc: 'Can take on limited work with flexible timelines', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30' },
  { value: 'busy', label: 'Busy', desc: 'Fully occupied, not taking on new projects', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [pushNotifs, setPushNotifs] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [availability, setAvailability] = useState('available');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/settings`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data.availability_status) setAvailability(data.availability_status);
        if (data.notifications_enabled !== undefined) setEmailNotifs(data.notifications_enabled);
        if (data.push_notifications !== undefined) setPushNotifs(data.push_notifications);
        if (data.weekly_digest !== undefined) setWeeklyDigest(data.weekly_digest);
      }
    } catch {}
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveAvailability = async (status) => {
    setAvailability(status);
    setSaving(true);
    try {
      await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ availability_status: status }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {} finally { setSaving(false); }
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }} className="max-w-2xl" data-testid="settings-page">
      <Breadcrumbs />
      <motion.div variants={fadeUp} className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and preferences.</p>
      </motion.div>

      {/* Profile */}
      <motion.div variants={fadeUp} className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <h2 className="font-heading text-base font-medium">Profile</h2>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card space-y-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center text-xl font-semibold">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-sm font-medium">{user?.name || 'User'}</p>
              <p className="text-xs text-muted-foreground">{user?.email || 'email@example.com'}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input defaultValue={user?.name || ''} className="mt-1.5 h-9" data-testid="settings-name-input" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input defaultValue={user?.email || ''} className="mt-1.5 h-9" disabled data-testid="settings-email-input" />
            </div>
          </div>
          <Button size="sm" className="h-9 gap-1.5" data-testid="save-profile-btn"><Save className="w-3.5 h-3.5" strokeWidth={2} /> Save Changes</Button>
        </div>
      </motion.div>

      {/* Calendar & Availability */}
      <motion.div variants={fadeUp} className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <h2 className="font-heading text-base font-medium">Calendar & Availability</h2>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card">
          <div className="mb-3">
            <p className="text-sm font-medium">Your availability</p>
            <p className="text-xs text-muted-foreground">This is shown to others in the Connections page.</p>
          </div>
          <div className="space-y-2" data-testid="availability-options">
            {AVAILABILITY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => saveAvailability(opt.value)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all duration-150 text-left
                  ${availability === opt.value
                    ? `${opt.color} border-current`
                    : 'border-border/50 bg-card hover:border-border text-muted-foreground hover:text-foreground'
                  }`}
                data-testid={`availability-${opt.value}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors
                  ${availability === opt.value ? 'border-current' : 'border-muted-foreground/30'}`}>
                  {availability === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-current" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-[11px] opacity-70">{opt.desc}</p>
                </div>
                {saving && availability === opt.value && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />}
                {saved && availability === opt.value && <Check className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} />}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Appearance */}
      <motion.div variants={fadeUp} className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <h2 className="font-heading text-base font-medium">Appearance</h2>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Theme</p>
              <p className="text-xs text-muted-foreground">Switch between light and dark mode</p>
            </div>
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={toggleTheme} data-testid="toggle-theme-settings-btn">
              <Monitor className="w-3.5 h-3.5" strokeWidth={2} />
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Notifications */}
      <motion.div variants={fadeUp} className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <h2 className="font-heading text-base font-medium">Notifications</h2>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card space-y-4">
          {[
            { label: 'Email notifications', desc: 'Receive updates via email', state: emailNotifs, setState: setEmailNotifs, id: 'email-notifs' },
            { label: 'Push notifications', desc: 'Browser push notifications', state: pushNotifs, setState: setPushNotifs, id: 'push-notifs' },
            { label: 'Weekly digest', desc: 'Summary email every Monday', state: weeklyDigest, setState: setWeeklyDigest, id: 'weekly-digest' },
          ].map(n => (
            <div key={n.id} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{n.label}</p>
                <p className="text-xs text-muted-foreground">{n.desc}</p>
              </div>
              <Switch checked={n.state} onCheckedChange={n.setState} data-testid={`setting-${n.id}`} />
            </div>
          ))}
        </div>
      </motion.div>

      {/* Security */}
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <h2 className="font-heading text-base font-medium">Security</h2>
        </div>
        <div className="p-5 rounded-xl border border-border/50 bg-card space-y-4">
          <div>
            <Label className="text-xs">Current Password</Label>
            <Input type="password" className="mt-1.5 h-9" data-testid="current-password-input" />
          </div>
          <div>
            <Label className="text-xs">New Password</Label>
            <Input type="password" className="mt-1.5 h-9" data-testid="new-password-input" />
          </div>
          <Button variant="outline" size="sm" className="h-9" data-testid="change-password-btn">Change Password</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
